import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Button,
  Table,
  Form,
  Pagination,
} from "react-bootstrap";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/TopBar";
import axiosInstance from "../../components/AxiosInstance";
import { toast } from "react-hot-toast";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const PAGE_SIZE = 10;

/**
 * Payment History (Credit) — the 3rd action icon ($) on /inhouse-accounts/agent.
 * Replaces the old "Auto Generated Invoice" screen; all invoice fields / APIs
 * were removed. Shows the credit updates made through
 * /api/agent-credit-limit/update — those are recorded in the inhouse-accounts
 * history as paymentType "Credit", so we read them back from
 * /api/inhouseAgentAccounts (filtered to this agent + credit entries). Same
 * card / table / search UI as the Payment History screen.
 */
export default function AgentsAutoGeneratedInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [agentInfo, setAgentInfo] = useState(null);

  const fetchCreditHistory = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: "0",
        limit: "1000", // fetch the agent's history once; filter/paginate client-side
        agentId: id,
      });
      const res = await axiosInstance.get(
        `/api/inhouseAgentAccounts?${params.toString()}`
      );
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.content)
        ? res.data.content
        : [];
      // Only the credit updates (logged with paymentType "Credit" when credit
      // is added via /api/agent-credit-limit/update). Backend already returns
      // newest-first; keep that order.
      const creditOnly = data.filter((r) =>
        String(r?.paymentType || "").toLowerCase().includes("credit")
      );
      setRecords(creditOnly);
    } catch (err) {
      toast.error("Failed to load payment history");
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgentInfo = async () => {
    try {
      const res = await axiosInstance.get(`/api/agent-credit-limit/agents`);
      if (Array.isArray(res.data)) {
        const match = res.data.find(
          (a) => String(a.agentId ?? a.id) === String(id)
        );
        setAgentInfo(match || null);
      } else if (res.data) {
        setAgentInfo(res.data);
      }
    } catch (err) {
      console.error("Failed to load agent info:", err);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCreditHistory();
      fetchAgentInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Reset to the first page whenever the remarks search changes.
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatAmount = (amount) => {
    if (amount == null || amount === "") return "0.00";
    return parseFloat(amount).toFixed(2);
  };

  // "Paid Through" — how the payment was actually received (Cash / Bank
  // Transfer / UPI / Cheque, etc.), captured on the Payment Mode field of
  // the Add Credit Limit form (registration/agent/view/{id}). Older rows
  // saved before that field existed have no value — show "N/A" rather than
  // leaving the cell blank.
  const getPaidThrough = (item) => item.paymentMode || item.paidThrough || "N/A";

  const handleBack = () => {
    navigate("/inhouse-accounts/agent");
  };

  // Remarks-based search + client-side pagination over the filtered list.
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) =>
      String(r?.remarks || "").toLowerCase().includes(q)
    );
  }, [records, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  return (
    <div className="min-vh-100 bg-light d-flex flex-column">
      <Topbar />
      <div className="d-flex flex-grow-1">
        <Sidebar />
        <main className="flex-grow-1 p-4">
          {/* Back Button */}
          <div className="mb-3">
            <Button
              variant="link"
              className="p-0 text-decoration-none d-flex align-items-center"
              onClick={handleBack}
            >
              <FaArrowLeft className="me-2" />
              Back to Agent Accounts
            </Button>
          </div>

          {/* Title */}
          <div className="mb-4">
            <h2 className="fw-bold mb-2">Payment History</h2>
            <p className="text-muted mb-1">
              Agent ID: <strong>{id || "Not found"}</strong>
            </p>
            {agentInfo && (
              <p className="text-muted mb-0">
                Agent:{" "}
                <strong>
                  {agentInfo.agentName || agentInfo.name || "Unknown Agent"}
                </strong>
              </p>
            )}
          </div>

          <Card className="shadow-sm rounded-xl">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Payment History</span>
              {/* Search Bar — by remarks */}
              <Form.Group className="hotel-search-bar">
                <Form.Control
                  type="text"
                  placeholder="Search by remarks..."
                  className="form-control-modern-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover striped className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>S/N</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Payment Type</th>
                    <th>Paid Through</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{index + 1 + safePage * PAGE_SIZE}</td>
                      <td>{formatDate(item.dateOfReceive || item.date)}</td>
                      <td>
                        ${formatAmount(item.amount)}
                      </td>
                      <td>{item.paymentType || "Unknown"}</td>
                      <td>{getPaidThrough(item)}</td>
                      <td className="text-muted">
                        {item.remarks || "No remarks"}
                      </td>
                    </tr>
                  ))}
                  {isLoading && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading payment history...
                      </td>
                    </tr>
                  )}
                  {!isLoading && filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No payment history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div>
                    <small className="text-muted">
                      Showing {pageItems.length} of {filtered.length} payments
                    </small>
                  </div>
                  <div>
                    <Pagination className="mb-0">
                      <Pagination.Prev
                        disabled={safePage === 0}
                        onClick={() => setPage(safePage - 1)}
                      />
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Pagination.Item
                          key={i}
                          active={i === safePage}
                          onClick={() => setPage(i)}
                        >
                          {i + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        disabled={safePage === totalPages - 1}
                        onClick={() => setPage(safePage + 1)}
                      />
                    </Pagination>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </main>
      </div>
    </div>
  );
}
